import { useTheme, useMediaQuery, SxProps, Theme } from '@mui/material';
import { useMemo } from 'react';
import DSBox from '../../../design-system/DSBox';
import { DSTypography } from '../../../design-system/Typography';
import DSDivider from '../../../design-system/DSDivider';
import DSRadio from '../../../design-system/DSRadio';
import { Plan } from '../../../api/plan';

const getPlanTitle = (plan: Plan): string => {
  if (plan.title) return plan.title;
  if (plan.upfrontAmount > 0 && plan.revSharePercentage > 0) return 'Hybrid';
  if (plan.upfrontAmount > 0) return 'Upfront Payment Only';
  return 'Revenue Share Only';
};

type PlanCardProps = {
  plan: Plan;
  selected: boolean;
  onClick?: () => void;
  sx?: SxProps<Theme>;
};

const PlanCard = ({ plan, selected, onClick, sx }: PlanCardProps) => {
  const theme = useTheme();
  const { palette } = theme;
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const hasUpfront = plan.upfrontAmount > 0;
  const hasRevShare = plan.revSharePercentage > 0;

  const cardSx = useMemo<SxProps<Theme>>(
    () => ({
      flex: '1 0 0',
      minWidth: 0,
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start',
      px: 2.5,
      pt: 3.5,
      pb: !isMobile && onClick ? 6 : 3.5,
      borderRadius: '20px',
      border: selected
        ? `2px solid ${palette.common.white}`
        : `1px solid ${palette.divider}`,
      backgroundColor: selected
        ? palette.background.paper
        : palette.background.default,
      cursor: onClick ? 'pointer' : 'default',
      gap: isMobile ? 0 : 4,
      ...sx,
    }),
    [selected, palette, onClick, isMobile, sx],
  );

  if (isMobile) {
    return (
      <DSBox
        onClick={onClick}
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
        onKeyDown={
          onClick
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onClick();
                }
              }
            : undefined
        }
        sx={cardSx}
      >
        <DSBox sx={{ width: '100%', mb: 1.5 }}>
          <DSTypography variant="h6" sx={{ color: 'text.secondary' }}>
            {getPlanTitle(plan)}
          </DSTypography>
          {plan.explanation && (
            <DSTypography variant="body2" sx={{ color: 'text.disabled' }}>
              {plan.explanation}
            </DSTypography>
          )}
        </DSBox>

        <DSBox
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
          }}
        >
          {hasUpfront && (
            <DSBox
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <DSTypography
                variant="h4"
                sx={{ color: palette.primary.main, whiteSpace: 'nowrap' }}
              >
                ${plan.upfrontAmount.toLocaleString()}
              </DSTypography>
              <DSTypography variant="body1" sx={{ color: 'text.secondary' }}>
                Upfront payment
              </DSTypography>
            </DSBox>
          )}

          {hasUpfront && hasRevShare && (
            <DSTypography variant="h5" sx={{ color: 'text.secondary' }}>
              +
            </DSTypography>
          )}

          {hasRevShare && (
            <DSBox
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <DSTypography
                variant="h4"
                sx={{ color: palette.primary.main, whiteSpace: 'nowrap' }}
              >
                {plan.revSharePercentage}%
              </DSTypography>
              <DSTypography variant="body1" sx={{ color: 'text.secondary' }}>
                Revenue share
              </DSTypography>
            </DSBox>
          )}
        </DSBox>
      </DSBox>
    );
  }

  return (
    <DSBox
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      sx={cardSx}
    >
      {/* Top: title + divider */}
      <DSBox
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}
      >
        <DSBox sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          <DSTypography
            variant="h6"
            sx={{
              color: 'text.secondary',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {getPlanTitle(plan)}
          </DSTypography>
          <DSBox sx={{ minHeight: '2.5rem', overflow: 'hidden' }}>
            {plan.explanation && (
              <DSTypography
                variant="body2"
                sx={{
                  color: 'text.disabled',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {plan.explanation}
              </DSTypography>
            )}
          </DSBox>
        </DSBox>
        <DSDivider sx={{ width: '100%', borderWidth: '1px' }} />
      </DSBox>

      {/* Middle: values */}
      <DSBox
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.75,
          width: '100%',
        }}
      >
        <DSBox
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: { md: 1, lg: 2, xl: 3 },
          }}
        >
          {hasUpfront && (
            <DSBox
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <DSTypography
                variant="h3"
                sx={{
                  color: palette.primary.main,
                  whiteSpace: 'nowrap',
                  fontSize: { md: '1.6rem', lg: '2.25rem', xl: '3rem' },
                }}
              >
                ${plan.upfrontAmount.toLocaleString()}
              </DSTypography>
              <DSTypography variant="body1" sx={{ color: 'text.secondary' }}>
                Upfront payment
              </DSTypography>
            </DSBox>
          )}
          {hasUpfront && hasRevShare && (
            <DSTypography
              variant="h5"
              sx={{
                color: 'text.secondary',
                fontSize: { md: '1rem', lg: '1.25rem', xl: '1.5rem' },
              }}
            >
              +
            </DSTypography>
          )}
          {hasRevShare && (
            <DSBox
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <DSTypography
                variant="h3"
                sx={{
                  color: palette.primary.main,
                  whiteSpace: 'nowrap',
                  fontSize: { md: '1.6rem', lg: '2.25rem', xl: '3rem' },
                }}
              >
                {plan.revSharePercentage}%
              </DSTypography>
              <DSTypography variant="body1" sx={{ color: 'text.secondary' }}>
                Revenue share
              </DSTypography>
            </DSBox>
          )}
        </DSBox>
        {!hasRevShare && (
          <DSTypography variant="h6" sx={{ color: 'text.primary' }}>
            No revenue share
          </DSTypography>
        )}
        {!hasUpfront && (
          <DSTypography variant="h6" sx={{ color: 'text.primary' }}>
            No upfront payment
          </DSTypography>
        )}
      </DSBox>

      {onClick !== undefined && (
        <DSRadio
          checked={selected}
          onChange={(e) => e.stopPropagation()}
          size="small"
          sx={{
            position: 'absolute',
            bottom: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            p: 0,
          }}
        />
      )}
    </DSBox>
  );
};

export default PlanCard;
